import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, DollarSign, Car, LayoutGrid, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function PricingTable({ title, icon: Icon, rules, onChange, t }) {
  const addRule = () => {
    const last = rules[rules.length - 1];
    const nextMin = last ? (last.max_days ? last.max_days + 1 : last.min_days + 30) : 1;
    onChange([...rules, { min_days: nextMin, max_days: null, price_per_day: 0, label: '' }]);
  };

  const removeRule = (idx) => {
    onChange(rules.filter((_, i) => i !== idx));
  };

  const updateRule = (idx, field, value) => {
    const updated = [...rules];
    if (field === 'max_days' && (value === '' || value === null)) {
      updated[idx] = { ...updated[idx], [field]: null };
    } else if (['min_days', 'max_days'].includes(field)) {
      updated[idx] = { ...updated[idx], [field]: value === '' ? '' : parseInt(value) || 0 };
    } else if (field === 'price_per_day') {
      updated[idx] = { ...updated[idx], [field]: value === '' ? '' : parseFloat(value) || 0 };
    } else {
      updated[idx] = { ...updated[idx], [field]: value };
    }
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-600" />
          <h3 className="font-bold text-slate-800 text-sm">{title}</h3>
        </div>
        <Button onClick={addRule} variant="outline" size="sm" className="rounded-full text-xs h-8" data-testid={`add-rule-${title}`}>
          <Plus className="w-3 h-3 mr-1" /> {t('addRule')}
        </Button>
      </div>

      {rules.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">Nessuna fascia configurata</p>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500 px-1">
            <div className="col-span-2">{t('minDays')}</div>
            <div className="col-span-2">{t('maxDays')}</div>
            <div className="col-span-3">{t('pricePerDay')}</div>
            <div className="col-span-4">{t('label')}</div>
            <div className="col-span-1"></div>
          </div>
          {rules.map((rule, idx) => (
            <div key={`${rule.min_days}-${rule.max_days}-${idx}`} className="grid grid-cols-12 gap-2 items-center bg-slate-50 rounded-xl p-2">
              <div className="col-span-2">
                <Input type="number" min={1} value={rule.min_days} className="rounded-lg h-8 text-xs"
                  onChange={e => updateRule(idx, 'min_days', e.target.value)} />
              </div>
              <div className="col-span-2">
                <Input type="number" min={1} placeholder={t('unlimited')}
                  value={rule.max_days === null ? '' : rule.max_days} className="rounded-lg h-8 text-xs"
                  onChange={e => updateRule(idx, 'max_days', e.target.value === '' ? null : e.target.value)} />
              </div>
              <div className="col-span-3">
                <div className="relative">
                  <Input type="number" step="0.01" min={0} value={rule.price_per_day}
                    className="rounded-lg h-8 text-xs pr-8"
                    onChange={e => updateRule(idx, 'price_per_day', e.target.value)} />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">CHF</span>
                </div>
              </div>
              <div className="col-span-4">
                <Input value={rule.label} className="rounded-lg h-8 text-xs"
                  onChange={e => updateRule(idx, 'label', e.target.value)}
                  placeholder="es. 1-5 giorni" />
              </div>
              <div className="col-span-1 flex justify-center">
                <Button variant="ghost" size="icon" onClick={() => removeRule(idx)}
                  className="h-7 w-7 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-full">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PricingPage() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pricing, setPricing] = useState({
    desk_daily_prices: [],
    desk_long_period_prices: [],
    parking_daily_prices: [],
    parking_long_period_prices: [],
  });

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/admin/pricing`, { withCredentials: true });
        setPricing({
          desk_daily_prices: data.desk_daily_prices || [],
          desk_long_period_prices: data.desk_long_period_prices || [],
          parking_daily_prices: data.parking_daily_prices || [],
          parking_long_period_prices: data.parking_long_period_prices || [],
        });
      } catch {
        toast.error('Error');
      } finally {
        setLoading(false);
      }
    };
    fetchPricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/pricing`, pricing, { withCredentials: true });
      toast.success(t('pricingSaved'));
    } catch {
      toast.error(t('error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-100px)]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pricing-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">{t('pricingManagement')}</h1>
            <p className="text-slate-500 text-sm mt-1">{t('toggleAvailability')}</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-pricing-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {t('savePricing')}
          </Button>
        </div>

        <div className="space-y-6">
          {/* Desk Pricing */}
          <Card className="card-floating">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-extrabold text-slate-900 flex items-center gap-2">
                <LayoutGrid className="w-5 h-5" /> {t('deskPricing')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <PricingTable
                title={t('dailyPrices')}
                icon={DollarSign}
                rules={pricing.desk_daily_prices}
                onChange={v => setPricing(p => ({ ...p, desk_daily_prices: v }))}
                t={t}
              />
              <div className="border-t pt-4">
                <PricingTable
                  title={t('longPeriodPrices') + ' (>30 ' + t('days') + ')'}
                  icon={DollarSign}
                  rules={pricing.desk_long_period_prices}
                  onChange={v => setPricing(p => ({ ...p, desk_long_period_prices: v }))}
                  t={t}
                />
              </div>
            </CardContent>
          </Card>

          {/* Parking Pricing */}
          <Card className="card-floating">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-extrabold text-slate-900 flex items-center gap-2">
                <Car className="w-5 h-5" /> {t('parkingPricing')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <PricingTable
                title={t('dailyPrices')}
                icon={DollarSign}
                rules={pricing.parking_daily_prices}
                onChange={v => setPricing(p => ({ ...p, parking_daily_prices: v }))}
                t={t}
              />
              <div className="border-t pt-4">
                <PricingTable
                  title={t('longPeriodPrices')}
                  icon={DollarSign}
                  rules={pricing.parking_long_period_prices}
                  onChange={v => setPricing(p => ({ ...p, parking_long_period_prices: v }))}
                  t={t}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
